import {Box, Button, makeStyles} from '@material-ui/core';
import cloneDeep from 'lodash/cloneDeep';
import {useIntl} from 'react-intl';
import {useInputs} from '@bellawatt/use-inputs';
import DropdownInput from '@generic/components/inputs/DropdownInput';
import ChargerSetInput from '@generic/components/inputs/ChargerSetInput';
import SliderInput from '@generic/components/inputs/SliderInput';
import SidebarInput from '@generic/components/inputs/SidebarInput';
import {formatAsDollars} from '@generic/functions/formatters';
import buildingTypes from '@generic/data/buildingTypes';
import currentRates from '@generic/data/currentRates';
import booleanOptions from '@generic/data/booleanOptions';
import voltageOptions from '@generic/data/voltageOptions';
import getDefaults, {DEFAULT_CHARGER_SET} from '@generic/functions/getDefaults';
import SmallSourceLink from '@generic/components/SmallSourceLink';

const CHARGER_SET_LIMIT = 5;

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '800',
    outline: 'none',
    textDecoration: 'underline',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.light,
      textDecoration: 'underline',
    },
    '&:active, &:focus': {
      color: theme.palette.primary.dark,
    },
  },
  resetButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    borderRadius: '10em',
    fontWeight: 800,
    textTransform: 'none',
    padding: '0.75rem 1.5rem',
    marginTop: theme.spacing(5),
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
    '&:active, &:focus': {
      backgroundColor: theme.palette.error.light,
    },
  },
}));

export default function PlanningInputs() {
  const {formatMessage} = useIntl();
  const {
    chargerSets,
    setInput,
    monthlyElectricBill,
    isOnMarketBasedRate,
    currentRate,
    voltageLevel,
    separatelyMetered,
    buildingType,
  } = useInputs();
  const classes = useStyles();

  const handleAddChargerSet = () => setInput({chargerSets: [...chargerSets, DEFAULT_CHARGER_SET]});

  const handleRemoveChargerSet = (idxToRemove) =>
    setInput({
      chargerSets: chargerSets.filter((_, idx) => idx !== idxToRemove),
    });

  const handleChangeChargerSet = (idx, data) => {
    const newChargerSets = cloneDeep(chargerSets);
    newChargerSets[idx] = data;
    setInput({chargerSets: newChargerSets});
  };

  const handleResetInputs = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(formatMessage({id: 'inputs.resetConfirmation'}))) {
      setInput({...getDefaults()});
    }
  };

  return (
    <Box>
      <SidebarInput title={formatMessage({id: 'planning.inputs.chargers.title'})}>
        <Box my={2} px={1}>
          {chargerSets.map((chargerSet, idx) => (
            <Box mb={2} key={idx}>
              <ChargerSetInput
                chargerSet={chargerSet}
                onChange={(data) => handleChangeChargerSet(idx, data)}
                onRemove={() => handleRemoveChargerSet(idx)}
              />
            </Box>
          ))}

          {chargerSets.length < CHARGER_SET_LIMIT && (
            <Button className={classes.button} onClick={handleAddChargerSet} type="button">
              {formatMessage({id: 'planning.inputs.chargers.addNewButton'})}
            </Button>
          )}
        </Box>
      </SidebarInput>

      <SidebarInput title={formatMessage({id: 'charging.inputs.advanced.title'})}>
        <Box mb={4} p={2}>
          <Box mb={5}>
            <DropdownInput
              title={formatMessage({
                id: 'charging.inputs.advanced.isOnMarketBasedRate.title',
              })}
              options={booleanOptions.map(({id, translationKey}) => ({
                value: id,
                children: formatMessage({id: translationKey}),
              }))}
              value={isOnMarketBasedRate ? 'yes' : 'no'}
              onChange={(e) => setInput({isOnMarketBasedRate: e.target.value === 'yes'})}
              mb={0}
            />
            <SmallSourceLink
              text={formatMessage({id: 'charging.inputs.advanced.isOnMarketBasedRate.sourceLink'})}
              link="https://lite.coned.com/_external/cerates/supply_charges.asp"
            />
          </Box>
          <DropdownInput
            title={formatMessage({
              id: 'charging.inputs.advanced.voltageLevel.title',
            })}
            tooltip={formatMessage({
              id: 'charging.inputs.advanced.voltageLevel.tooltip',
            })}
            options={voltageOptions.map(({id, translationKey}) => ({
              value: id,
              children: formatMessage({id: translationKey}),
            }))}
            value={voltageLevel}
            onChange={(e) => setInput({voltageLevel: e.target.value})}
            mb={5}
          />
          <DropdownInput
            title={formatMessage({
              id: 'charging.inputs.advanced.separatelyMetered.title',
            })}
            tooltip={formatMessage({
              id: 'charging.inputs.advanced.separatelyMetered.tooltip',
            })}
            options={booleanOptions.map(({id, translationKey}) => ({
              value: id,
              children: formatMessage({id: translationKey}),
            }))}
            value={separatelyMetered ? 'yes' : 'no'}
            onChange={(e) => setInput({separatelyMetered: e.target.value === 'yes'})}
            mb={5}
          />
          {!separatelyMetered && (
            <>
              <DropdownInput
                title={formatMessage({
                  id: 'charging.inputs.advanced.buildingType.title',
                })}
                tooltip={formatMessage({
                  id: 'charging.inputs.advanced.buildingType.tooltip',
                })}
                options={buildingTypes.map(({id, translationKey}) => ({
                  value: id,
                  children: formatMessage({id: translationKey}),
                }))}
                value={buildingType}
                onChange={(e) => setInput({buildingType: e.target.value})}
                mb={5}
              />
              <DropdownInput
                title={formatMessage({
                  id: 'charging.inputs.advanced.currentRate.title',
                })}
                tooltip={formatMessage({
                  id: 'charging.inputs.advanced.currentRate.tooltip',
                })}
                options={currentRates.map(({id, translationKey}) => ({
                  value: id,
                  children: formatMessage({id: translationKey}),
                }))}
                value={currentRate.name}
                onChange={(e) => setInput({currentRate: e.target.value})}
                mb={5}
              />
              <SliderInput
                label={formatMessage({
                  id: 'charging.inputs.advanced.monthlyBillSliderLabel',
                })}
                tooltip={formatMessage({
                  id: 'charging.inputs.advanced.monthlyBillSliderTooltip',
                })}
                value={monthlyElectricBill}
                formatFn={formatAsDollars}
                onChange={(value) => setInput({monthlyElectricBill: value})}
                min={1000}
                max={20000}
                step={1000}
              />
            </>
          )}

          <Button className={classes.resetButton} onClick={handleResetInputs}>
            {formatMessage({id: 'inputs.reset'})}
          </Button>
        </Box>
      </SidebarInput>
    </Box>
  );
}
